import React, { useState, useEffect } from "react";
import {
  useSearchParams,
  useLocation,
  useNavigate,
  Link as RouterLink,
} from "react-router-dom";
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Alert,
  Tabs,
  Tab,
  Snackbar,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import HistoryIcon from "@mui/icons-material/History";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import axios from "axios";
import ConfigForm from "./components/ConfigForm";
import AnalysisProgress from "./components/AnalysisProgress";
import ConfigStatus from "./components/ConfigStatus";
import WorkspaceSwitcher from "./components/WorkspaceSwitcher";
import JobQueueDrawer from "./components/JobQueueDrawer";
import ReportCarousel from "./components/ReportCarousel";
import HistoryDashboard from "./components/HistoryDashboard";
import HistoryDetail from "./components/HistoryDetail";
import CompareReports from "./components/CompareReports";
import WatchlistsPage from "./components/WatchlistsPage";
import WatchlistDetail from "./components/WatchlistDetail";
import TradingTab from "./components/TradingTab";
import EnsembleTradingPanel from "./components/EnsembleTradingPanel";
import AuthWrapper from "./components/AuthWrapper";

const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#00ff00",
    },
    secondary: {
      main: "#00bfff",
    },
    background: {
      default: "#0a0a0a",
      paper: "#1a1a1a",
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      color: "#00ff00",
      fontWeight: "bold",
    },
    h6: {
      color: "#00bfff",
    },
  },
});

// The tab strip is addressable: each tab has its own path so tabs can be linked
// to and the browser back button (e.g. from /stock/:ticker) lands where it came from.
const TAB_PATHS = ["/", "/history", "/compare", "/watchlists", "/trading"];

const tabFromPath = (pathname) => {
  const index = TAB_PATHS.indexOf(pathname.replace(/\/+$/, "") || "/");
  return index === -1 ? 0 : index;
};

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  // Derived from the URL rather than held in state, so the two can't drift.
  const activeTab = tabFromPath(location.pathname);

  // --- queue model ---
  // Each entry: { ticker, sessionId, status, analysisDate, startedAt, analysisData, error, selectedAgents }
  // Persisted to localStorage so a page refresh doesn't lose in-flight jobs.
  const [queuedJobs, setQueuedJobs] = useState(() => {
    try {
      const saved = localStorage.getItem("tradingAgentsQueue");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  // The job whose status/results the user is currently viewing (null = show form)
  const [selectedJob, setSelectedJob] = useState(null);

  // Legacy single-analysis state (kept for save/snackbar logic)
  const [saving, setSaving] = useState(false);
  const [saveSnackbar, setSaveSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [error, setError] = useState(null);
  const [selectedHistoryId, setSelectedHistoryId] = useState(null);
  const [selectedWatchlistId, setSelectedWatchlistId] = useState(null);
  const [searchParams] = useSearchParams();

  const [compareInitial, setCompareInitial] = useState(null);
  const [analyzeTicker, setAnalyzeTicker] = useState(null);

  useEffect(() => {
    const r1 = searchParams.get("compare");
    const r2 = searchParams.get("compare2");
    const compareTicker = searchParams.get("ticker");
    const analyzeParam = searchParams.get("analyze");
    if (r1 && r2) {
      setCompareInitial({
        reportId1: parseInt(r1, 10),
        reportId2: parseInt(r2, 10),
        ticker: compareTicker || "",
      });
      // Also drops the query string now that it's been consumed.
      navigate("/compare", { replace: true });
    } else if (analyzeParam) {
      setAnalyzeTicker(analyzeParam.toUpperCase());
      navigate("/", { replace: true });
    }
  }, [searchParams, navigate]);

  // Persist queue to localStorage so refreshes don't lose in-flight jobs
  useEffect(() => {
    localStorage.setItem("tradingAgentsQueue", JSON.stringify(queuedJobs));
  }, [queuedJobs]);

  // Re-attach polling for any jobs that were still running before a refresh
  useEffect(() => {
    queuedJobs
      .filter((j) => j.status === "queued" || j.status === "in_progress")
      .forEach((job) => pollJob(job));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount only

  const handleTabChange = (event, newValue) => {
    navigate(TAB_PATHS[newValue]);
    setSelectedHistoryId(null);
    setSelectedWatchlistId(null);
  };

  const handleViewHistoryReport = (analysisId) => {
    setSelectedHistoryId(analysisId);
  };

  const handleBackToHistory = () => {
    setSelectedHistoryId(null);
  };

  const handleSaveAnalysis = async () => {
    const sid = selectedJob?.sessionId;
    if (!sid) {
      setSaveSnackbar({ open: true, message: "No active session to save", severity: "error" });
      return;
    }
    setSaving(true);
    try {
      const response = await axios.post(`/api/save/${sid}`);
      if (response.data.success) {
        setSaveSnackbar({
          open: true,
          message: `Analysis saved to history (ID: ${response.data.analysis_id})`,
          severity: "success",
        });
      } else {
        setSaveSnackbar({ open: true, message: response.data.error || "Failed to save", severity: "error" });
      }
    } catch (err) {
      setSaveSnackbar({ open: true, message: err.response?.data?.error || "Failed to save analysis", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleCloseSaveSnackbar = () => {
    setSaveSnackbar({ ...saveSnackbar, open: false });
  };

  // Update a single queued job by sessionId (keeps the array ref stable for polling closures)
  const updateQueuedJob = (sessionId, patch) => {
    setQueuedJobs((prev) =>
      prev.map((j) => (j.sessionId === sessionId ? { ...j, ...patch } : j))
    );
    // Also patch the selectedJob ref if it's the currently viewed one
    setSelectedJob((prev) =>
      prev?.sessionId === sessionId ? { ...prev, ...patch } : prev
    );
  };

  const pollJob = (job) => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await axios.get(`/api/status/${job.sessionId}`);
        const raw = response.data;
        const newStatus =
          raw.status === "completed" ? "completed"
          : raw.status === "error" ? "error"
          : raw.status === "in_progress" ? "in_progress"
          : job.status;

        if (newStatus === "completed") {
          updateQueuedJob(job.sessionId, { status: "completed", analysisData: raw.data });
          clearInterval(pollInterval);
        } else if (newStatus === "error") {
          updateQueuedJob(job.sessionId, { status: "error", error: raw.error || "Analysis failed" });
          clearInterval(pollInterval);
        } else {
          updateQueuedJob(job.sessionId, { status: newStatus, analysisData: raw.data });
        }
      } catch {
        updateQueuedJob(job.sessionId, { status: "error", error: "Failed to get status" });
        clearInterval(pollInterval);
      }
    }, 2000);

    // Hard stop after 10 minutes
    setTimeout(() => clearInterval(pollInterval), 600000);
  };

  const handleStartAnalysis = async (formConfig) => {
    setError(null);
    const tickers = formConfig.tickers
      ? formConfig.tickers
      : [formConfig.ticker];

    for (const ticker of tickers) {
      const { tickers: _t, ...baseConfig } = formConfig;
      const payload = { ticker, ...baseConfig };
      try {
        const response = await axios.post("/api/analyze", payload);
        if (response.data.success) {
          const newJob = {
            ticker,
            sessionId: response.data.session_id,
            status: response.data.cached ? "completed" : "queued",
            analysisDate: formConfig.analysis_date || "",
            startedAt: Date.now(),
            analysisData: null,
            error: null,
            selectedAgents: formConfig.selected_agents || null,
          };
          setQueuedJobs((prev) => [...prev, newJob]);
          if (!response.data.cached) pollJob(newJob);
        } else {
          setError(response.data.error || `Failed to start analysis for ${ticker}`);
        }
      } catch (err) {
        setError(err.response?.data?.error || `Failed to start analysis for ${ticker}`);
      }
    }
  };

  const handleSelectJob = (job) => {
    setSelectedJob(job);
    setError(null);
  };

  const handleCloseJobView = () => {
    setSelectedJob(null);
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <AuthWrapper>
        <Container maxWidth="lg" sx={{ py: { xs: 1, sm: 2, md: 4 }, px: { xs: 0.5, sm: 2 } }}>
          <Paper
            elevation={3}
            sx={{ p: { xs: 1, sm: 2, md: 4 }, backgroundColor: "background.paper", overflow: 'hidden' }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
              <WorkspaceSwitcher />
            </Box>
            <Box textAlign="center" mb={2}>
              <Typography
                variant="h4"
                gutterBottom
                component={RouterLink}
                to="/"
                sx={{
                  textDecoration: 'none',
                  color: 'inherit',
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.8,
                    transition: 'opacity 0.2s ease',
                  },
                }}
              >
                Multi-Agents Financial Platform
              </Typography>
            </Box>

            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  "& .MuiTab-root": {
                    color: "text.secondary",
                    "&.Mui-selected": {
                      color: "primary.main",
                    },
                  },
                  "& .MuiTabs-indicator": {
                    backgroundColor: "primary.main",
                  },
                }}
              >
                <Tab
                  icon={<AddCircleIcon />}
                  iconPosition="start"
                  label="New Analysis"
                />
                <Tab
                  icon={<HistoryIcon />}
                  iconPosition="start"
                  label="History"
                />
                <Tab
                  icon={<CompareArrowsIcon />}
                  iconPosition="start"
                  label="Compare"
                />
                <Tab
                  icon={<BookmarkIcon />}
                  iconPosition="start"
                  label="Watchlists"
                />
                <Tab
                  icon={<ShowChartIcon />}
                  iconPosition="start"
                  label="Trading"
                />
              </Tabs>
            </Box>

            {activeTab === 0 && (
              <>
                {error && (
                  <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                  </Alert>
                )}

                {/* Job viewer: shown when user clicks a job in the drawer */}
                {selectedJob ? (
                  <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="h5" color="primary">
                        {selectedJob.ticker} — {
                          selectedJob.status === "completed" ? "Results" :
                          selectedJob.status === "error" ? "Error" : "In Progress"
                        }
                      </Typography>
                      <Box display="flex" gap={2}>
                        {selectedJob.status === "completed" && (
                          <Button
                            variant="contained"
                            color="primary"
                            startIcon={<SaveIcon />}
                            onClick={handleSaveAnalysis}
                            disabled={saving}
                          >
                            {saving ? "Saving..." : "Save to History"}
                          </Button>
                        )}
                        <Button variant="outlined" onClick={handleCloseJobView}>
                          ← New Analysis
                        </Button>
                      </Box>
                    </Box>

                    {selectedJob.status === "completed" && selectedJob.analysisData ? (
                      <ReportCarousel analysisData={selectedJob.analysisData} title="Analysis Reports" />
                    ) : selectedJob.status === "error" ? (
                      <Alert severity="error">{selectedJob.error || "Analysis failed"}</Alert>
                    ) : (
                      <AnalysisProgress
                        config={{ ticker: selectedJob.ticker, analysis_date: selectedJob.analysisDate, selected_agents: selectedJob.selectedAgents }}
                        analysisData={selectedJob.analysisData}
                        isAnalyzing={true}
                        sessionId={selectedJob.sessionId}
                        analysisStatus={selectedJob.status}
                      />
                    )}
                  </Box>
                ) : (
                  /* Default: always show the form so new analyses can be submitted */
                  <>
                    <Box mb={3}>
                      <ConfigStatus />
                    </Box>
                    <ConfigForm onSubmit={handleStartAnalysis} initialTicker={analyzeTicker} />
                  </>
                )}

                <Snackbar
                  open={saveSnackbar.open}
                  autoHideDuration={6000}
                  onClose={handleCloseSaveSnackbar}
                  anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                >
                  <Alert onClose={handleCloseSaveSnackbar} severity={saveSnackbar.severity} sx={{ width: "100%" }}>
                    {saveSnackbar.message}
                  </Alert>
                </Snackbar>
              </>
            )}

            {activeTab === 1 && (
              <>
                {selectedHistoryId ? (
                  <HistoryDetail
                    analysisId={selectedHistoryId}
                    onBack={handleBackToHistory}
                  />
                ) : (
                  <HistoryDashboard onViewReport={handleViewHistoryReport} />
                )}
              </>
            )}

            {activeTab === 2 && (
              <CompareReports
                initialCompare={compareInitial}
                onConsumeInitial={() => setCompareInitial(null)}
              />
            )}

            {activeTab === 3 && (
              <>
                {selectedWatchlistId ? (
                  <WatchlistDetail
                    watchlistId={selectedWatchlistId}
                    onBack={() => setSelectedWatchlistId(null)}
                  />
                ) : (
                  <WatchlistsPage onSelectWatchlist={(id) => setSelectedWatchlistId(id)} />
                )}
              </>
            )}

            {activeTab === 4 && (
              <>
                <EnsembleTradingPanel />
                <TradingTab />
              </>
            )}
          </Paper>
        </Container>

        {/* Floating queue drawer — always mounted, independent of tab */}
        <JobQueueDrawer
          jobs={queuedJobs}
          selectedJobId={selectedJob?.sessionId}
          onSelectJob={(job) => {
            navigate("/");
            handleSelectJob(job);
          }}
        />
      </AuthWrapper>
    </ThemeProvider>
  );
}

export default App;
